/**
 * Feature search — "type a value, find the feature", across many layers at once.
 *
 * WHAT MAKES THIS DIFFERENT FROM THE QUERY TOOL
 * Query asks someone to BUILD a condition: pick a layer, pick a field, pick an
 * operator, type a value, press Run. That is the right shape when you know what
 * you are looking for and how it is stored. It is the wrong shape when you have
 * a meter number on a work order and simply want to see it on the map — there,
 * every one of those four choices is a question the person cannot answer and
 * should not have to. So this searches EVERY configured field of EVERY
 * configured layer at once and offers what it found, the way a site search does.
 *
 * WHAT THE ADMIN CONFIGURES, AND WHY IT MATTERS HERE MORE THAN ELSEWHERE
 * Searching every field of every layer on each keystroke is one request per
 * layer, and a service will happily scan a text column it has no index on. The
 * `layers` and `fields` settings are therefore not decoration — they are what
 * keeps this cheap. An empty setting still means "all", following the same
 * convention the rest of the tools use, but the Tool Library says plainly that
 * naming the fields is the difference between fast and slow.
 *
 * THREE BACKENDS, THREE DIALECTS
 *   file-backed  the features are already here — matched in memory
 *   ArcGIS       `where` with UPPER(field) LIKE '%TERM%'
 *   GeoServer    CQL_FILTER with field ILIKE '%term%'
 *
 * Both remote dialects have a case-insensitive form that MOST services support
 * and some do not: `UPPER()` is absent from a few Esri data sources, and `ILIKE`
 * is a GeoServer extension older versions lack. Rather than pick the lowest
 * common denominator — a case-SENSITIVE search, which for a person typing a
 * street name is simply a broken search — each request falls back to the plain
 * form only after the preferred one is refused. The cost is one wasted request
 * against an old service; the alternative is that "kumar" never finds "KUMAR".
 */

import type { Layer } from '../types';
import { arcgisServiceUrlFor, getArcgisBaseUrl, withArcgisToken } from '../utils/arcgis';
import { getLayerDisplayName } from '../layers/capabilities';
import { filteredLocalFeatures } from '../layers/localFilter';

/** One row in the suggestion list. */
export interface SearchHit {
  /** Stable across renders, and unique across layers. */
  key: string;
  layerId: string;
  /** The layer's display name, shown when more than one layer is searched. */
  layerName: string;
  /** The value that matched — the text the row reads as. */
  label: string;
  /** Which field it matched on. */
  field: string;
  feature: GeoJSON.Feature;
}

/** One layer to search, with the fields already narrowed to that layer's own. */
export interface SearchTarget {
  layer: Layer;
  fields: string[];
  /** Of those, the ones the service publishes as numbers. See below. */
  numericFields: Set<string>;
}

export interface SearchRequest {
  term: string;
  targets: SearchTarget[];
  geoServerUrl: string;
  arcgisUrl: string;
  /** Total suggestions wanted, across all layers. */
  limit: number;
}

/** Doubles single quotes — the escape both SQL and CQL use inside a literal. */
function quote(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

/** Combines the tool's search with whatever filter the layer already carries. */
function withLayerFilter(search: string, layerFilter: string | undefined): string {
  const existing = layerFilter?.trim();
  return existing ? `(${existing}) AND (${search})` : search;
}

/**
 * Reads a property by name, tolerating a difference in case.
 *
 * The schema and the records do not always agree. Esri's sample States layer
 * publishes its columns as `state_name` while plenty of services publish
 * `STATE_NAME`, and a `where` clause matches either because SQL identifiers are
 * case-insensitive — so the SEARCH succeeds and then the exact-case lookup that
 * builds the row finds nothing, and every suggestion reads as a dash. Checked
 * against the live service: this is not hypothetical.
 *
 * The lowered index is built once per feature, and only if an exact hit misses.
 */
function propertyReader(properties: Record<string, unknown>) {
  let lowered: Map<string, unknown> | null = null;

  return (field: string): string => {
    let value = properties[field];
    if (value === undefined) {
      if (!lowered) {
        lowered = new Map();
        for (const [key, entry] of Object.entries(properties)) lowered.set(key.toLowerCase(), entry);
      }
      value = lowered.get(field.toLowerCase());
    }
    if (value === undefined || value === null) return '';
    return String(value).trim();
  };
}

/** Identity for a feature, so React keys are stable and two hits never collide. */
function featureKey(layerId: string, feature: GeoJSON.Feature, index: number): string {
  const properties = (feature.properties ?? {}) as Record<string, unknown>;
  const id =
    feature.id ??
    properties.OBJECTID ??
    properties.objectid ??
    properties.FID ??
    properties.fid ??
    `i${index}`;
  return `${layerId}:${String(id)}`;
}

/* ─────────────────────────────── file-backed ────────────────────────────── */

/**
 * Matched in memory, honouring the layer's own filter.
 *
 * `filteredLocalFeatures` rather than `localData.features` directly: a layer
 * narrowed to one ward should not offer results from outside it, or clicking a
 * suggestion flies the map to a feature that is not drawn.
 */
function searchLocal(target: SearchTarget, term: string, limit: number): SearchHit[] {
  const needle = term.toLowerCase();
  const layerName = getLayerDisplayName(target.layer);
  const hits: SearchHit[] = [];

  const features = filteredLocalFeatures(target.layer);
  for (let index = 0; index < features.length && hits.length < limit; index += 1) {
    const feature = features[index];
    const read = propertyReader((feature.properties ?? {}) as Record<string, unknown>);

    // FIRST matching field only: one feature is one suggestion, however many
    // of its columns happen to contain the text.
    for (const field of target.fields) {
      const text = read(field);
      if (!text || !text.toLowerCase().includes(needle)) continue;

      hits.push({
        key: featureKey(target.layer.id, feature, index),
        layerId: target.layer.id,
        layerName,
        label: text,
        field,
        feature,
      });
      break;
    }
  }

  return hits;
}

/* ───────────────────────────────── ArcGIS ───────────────────────────────── */

/**
 * The `where` for one attempt.
 *
 * Numeric columns cannot take LIKE, and `OBJECTID LIKE '%12%'` is an error, not
 * a miss — one bad clause fails the WHOLE request and the layer returns nothing.
 * So a numeric field only contributes a clause when the typed text is actually
 * a number, and then as equality.
 */
function arcgisWhere(target: SearchTarget, term: string, upper: boolean): string | null {
  const asNumber = Number(term);
  const numeric = term.trim() !== '' && Number.isFinite(asNumber);
  const parts: string[] = [];

  for (const field of target.fields) {
    if (target.numericFields.has(field)) {
      if (numeric) parts.push(`${field} = ${asNumber}`);
      continue;
    }
    parts.push(
      upper
        ? `UPPER(${field}) LIKE ${quote(`%${term.toUpperCase()}%`)}`
        : `${field} LIKE ${quote(`%${term}%`)}`,
    );
  }

  if (parts.length === 0) return null;
  return withLayerFilter(parts.join(' OR '), target.layer.filter);
}

async function fetchArcgis(
  target: SearchTarget,
  term: string,
  arcgisUrl: string,
  limit: number,
  signal: AbortSignal,
): Promise<SearchHit[]> {
  const effectiveUrl = arcgisServiceUrlFor(target.layer, { arcgisUrl });
  const baseUrl = getArcgisBaseUrl(effectiveUrl);
  if (!baseUrl || target.layer.arcgisLayerId === undefined) return [];

  const attempt = async (upper: boolean) => {
    const where = arcgisWhere(target, term, upper);
    if (!where) return { ok: true as const, features: [] as GeoJSON.Feature[] };

    const params = new URLSearchParams({
      where,
      outFields: '*',
      outSR: '4326',
      returnGeometry: 'true',
      f: 'geojson',
      resultRecordCount: String(limit),
    });

    const response = await fetch(withArcgisToken(`${baseUrl}/${target.layer.arcgisLayerId}/query?${params}`, effectiveUrl), {
      signal,
    });
    if (!response.ok) return { ok: false as const, features: [] };

    const data = await response.json();
    // Esri answers a rejected `where` with HTTP 200 and an error body, so the
    // status code alone cannot tell a bad expression from an empty result.
    if (data?.error) return { ok: false as const, features: [] };
    return { ok: true as const, features: (data?.features ?? []) as GeoJSON.Feature[] };
  };

  let result = await attempt(true);
  // UPPER() is unsupported on a few data sources. Retry case-sensitively rather
  // than report "no results" for a query the service simply could not parse.
  if (!result.ok) result = await attempt(false);
  if (!result.ok) return [];

  return toHits(target, result.features, term);
}

/* ──────────────────────────────── GeoServer ─────────────────────────────── */

function cqlFilter(target: SearchTarget, term: string, insensitive: boolean): string | null {
  const parts = target.fields.map((field) =>
    insensitive
      ? `${field} ILIKE ${quote(`%${term}%`)}`
      : `${field} LIKE ${quote(`%${term}%`)}`,
  );
  if (parts.length === 0) return null;
  return withLayerFilter(parts.join(' OR '), target.layer.filter);
}

async function fetchWfs(
  target: SearchTarget,
  term: string,
  geoServerUrl: string,
  limit: number,
  signal: AbortSignal,
): Promise<SearchHit[]> {
  if (!geoServerUrl.trim()) return [];

  const attempt = async (insensitive: boolean) => {
    const filter = cqlFilter(target, term, insensitive);
    if (!filter) return { ok: true as const, features: [] as GeoJSON.Feature[] };

    try {
      const url = new URL(geoServerUrl.trim());
      const path = url.pathname.replace(/\/+$/, '');
      url.pathname = /\/(wms|wfs)$/i.test(path)
        ? path.replace(/\/(wms|wfs)$/i, '/wfs')
        : `${path}/wfs`;
      url.search = '';
      url.searchParams.set('service', 'WFS');
      url.searchParams.set('request', 'GetFeature');
      url.searchParams.set('version', '2.0.0');
      url.searchParams.set('typeNames', target.layer.name);
      url.searchParams.set('outputFormat', 'application/json');
      url.searchParams.set('srsName', 'EPSG:4326');
      url.searchParams.set('count', String(limit));
      url.searchParams.set('CQL_FILTER', filter);

      const response = await fetch(url.toString(), { signal });
      if (!response.ok) return { ok: false as const, features: [] };
      const data = await response.json();
      return { ok: true as const, features: (data?.features ?? []) as GeoJSON.Feature[] };
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') throw error;
      return { ok: false as const, features: [] };
    }
  };

  let result = await attempt(true);
  // ILIKE is a GeoServer extension; older versions reject it outright.
  if (!result.ok) result = await attempt(false);
  if (!result.ok) return [];

  return toHits(target, result.features, term);
}

/* ──────────────────────────────── shared ────────────────────────────────── */

/**
 * Turns service features into rows, naming the field the value came from.
 *
 * The service matched; it does not say WHICH column matched, so that is worked
 * out here. When nothing visibly contains the text — a numeric equality match,
 * or a service whose LIKE is looser than the check below — the first configured
 * field is used, so the row still reads as something rather than as blank.
 */
function toHits(target: SearchTarget, features: GeoJSON.Feature[], term: string): SearchHit[] {
  const needle = term.toLowerCase();
  const layerName = getLayerDisplayName(target.layer);

  return features.map((feature, index) => {
    const read = propertyReader((feature.properties ?? {}) as Record<string, unknown>);

    let matched: string | null = null;
    let label = '';
    let fallback: { field: string; text: string } | null = null;

    for (const field of target.fields) {
      const text = read(field);
      if (!text) continue;
      if (!fallback) fallback = { field, text };
      if (text.toLowerCase().includes(needle)) {
        matched = field;
        label = text;
        break;
      }
    }

    /* Nothing visibly contains the text: a numeric equality match, or a service
       whose LIKE is looser than the check above. The first field that has a
       value is still one the ADMIN chose, so the row reads as something real
       rather than as a dash. */
    if (!matched && fallback) {
      matched = fallback.field;
      label = fallback.text;
    }

    return {
      key: featureKey(target.layer.id, feature, index),
      layerId: target.layer.id,
      layerName,
      label: label || '—',
      field: matched ?? target.fields[0] ?? '',
      feature,
    };
  });
}

/**
 * Round-robin, so every layer is represented in a short list.
 *
 * Taking the first N of a concatenated list looks fine until one layer has
 * hundreds of matches: it fills all six slots and the other layers appear not
 * to have been searched at all. Rotating means a six-row list across four
 * layers shows something from each.
 */
function interleave(groups: SearchHit[][], limit: number): SearchHit[] {
  const merged: SearchHit[] = [];
  const deepest = Math.max(0, ...groups.map((group) => group.length));

  for (let round = 0; round < deepest && merged.length < limit; round += 1) {
    for (const group of groups) {
      if (round >= group.length) continue;
      merged.push(group[round]);
      if (merged.length >= limit) break;
    }
  }

  return merged;
}

/**
 * Searches every target and returns at most `limit` suggestions.
 *
 * One layer failing — a service down, a field that no longer exists — costs
 * that layer's results and nothing else. A search box that goes blank because
 * one of five layers is unreachable would be a worse answer than a short list.
 */
export async function searchFeatures(
  request: SearchRequest,
  signal: AbortSignal,
): Promise<SearchHit[]> {
  const term = request.term.trim();
  if (!term) return [];

  const perLayer = Math.max(1, request.limit);

  const groups = await Promise.all(
    request.targets.map(async (target) => {
      if (target.fields.length === 0) return [];
      try {
        if (target.layer.localData) return searchLocal(target, term, perLayer);
        if (target.layer.arcgisLayerId !== undefined) {
          return await fetchArcgis(target, term, request.arcgisUrl, perLayer, signal);
        }
        return await fetchWfs(target, term, request.geoServerUrl, perLayer, signal);
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') throw error;
        console.warn(`[search] "${target.layer.name}" could not be searched`, error);
        return [];
      }
    }),
  );

  return interleave(groups, request.limit);
}
